// ============================================================
// DAMAGE CALC - Per-hit damage pipeline. Pure apart from the dice.
// Extracted from useCombat handleAttack damage loop.
//
// The stages live beside it, in the order a hit runs through them:
// hitDamageRoll (context, force field, dice, raw damage), hitSoak
// (cover, armour, penetration, Toughness) and hitOutcome (the clamps,
// Righteous Fury, the critical). This file orchestrates them and
// assembles the `HitResult`, whose key order it keeps stable.
// ============================================================
import { hitContext, checkForceField, rollHitDice, rawDamageOf, DEVASTATING_BONUS, SCATTER_MOD } from './hitDamageRoll';
import type { ForceFieldCheck, RawDamage } from './hitDamageRoll';
import { soakOf } from './hitSoak';
import type { HitSoak } from './hitSoak';
import { landedDamage, rollFury, critOutcome } from './hitOutcome';
import type { CritOutcome, FuryRoll, LandedDamage } from './hitOutcome';
import type { CalcSingleHitParams, HitContext, HitResult } from './hitDamageTypes';
import type { DiceResult } from '../types';

export type { HitResult, CalcSingleHitParams } from './hitDamageTypes';

/**
 * Calculate damage for a single hit. Handles force fields, Graviton, Accurate,
 * Tearing, Proven, Primitive, damage calc, Devastating, Maximal, Scatter,
 * cover AP, Lance/WarpWeapon/Graviton AP bypass, Felling, Daemonic protection,
 * Incorporeal, Force bonus, Righteous Fury, Vengeful, Hatred, crits, True Grit,
 * Stuff of Nightmares.
 *
 * Dice, in order: the 'A' damage type, the field's d100, the damage roll
 * and its re-roll, Accurate's d10s, Maximal's d10, the fury's d100 and d5.
 */
export function calcSingleHitDamage(params: CalcSingleHitParams): HitResult {
  const ctx = hitContext(params);
  const field = checkForceField(params.target, ctx.sp, ctx.exposed);
  if (field.ffBlocked) return fieldBlockedHit(params, ctx.damageType, field);
  const { dmgRoll, accurateDice } = rollHitDice(params, ctx);
  const raw = rawDamageOf(params, ctx, dmgRoll);
  const soak = soakOf(params, ctx, raw.predicate.pen);
  const landed = landedDamage(ctx, params.target, raw.rawDamage, soak);
  const fury = rollFury(params, ctx, dmgRoll);
  const outcome = critOutcome(params, ctx, landed.effectiveDamage, fury);
  const stages = { ctx, field, dmgRoll, accurateDice, raw, soak, landed, fury, outcome };
  return { ...hitHeadline(params, stages), ...hitBreakdown(params, stages) };
}

/** Every stage's result, for the two halves of the assembly. */
interface HitStages {
  ctx: HitContext;
  field: ForceFieldCheck;
  dmgRoll: DiceResult;
  accurateDice: number;
  raw: RawDamage;
  soak: HitSoak;
  landed: LandedDamage;
  fury: FuryRoll;
  outcome: CritOutcome;
}

/** The result's first half: what the hit did. Key order is the log's; keep it. */
function hitHeadline(params: CalcSingleHitParams, s: HitStages) {
  const { raw, soak, field, outcome } = s;
  return {
    location: params.location,
    damageType: s.ctx.damageType,
    dmgRoll: s.dmgRoll,
    rawDamage: raw.rawDamage,
    ...(raw.rawReduction ? { rawReduction: raw.rawReduction } : {}),
    ap: soak.ap,
    effectiveAP: soak.effectiveAP,
    coverAP: soak.coverAP,
    tb: soak.tb,
    effectiveDamage: outcome.effectiveDamage,
    penetration: params.pen,
    ...s.fury,
    ffBlocked: false,
    ffRoll: field.ffRoll,
    ffOverloaded: false,
    ffProtection: field.ffProtection,
    ffOverloadThreshold: field.ffOverloadThreshold,
    crit: outcome.crit,
    critSev: outcome.critSev,
  };
}

/**
 * The result's second half: the breakdown the narrative prints. The
 * optional shares are present only when they fired - the
 * conditional-spread idiom keeps Firestore payloads undefined-free.
 * `soakCapReduction` is reported rather than left implicit, in the same
 * idiom as `fellingReduction`: the components are the values that
 * actually applied, so without it the log would show a base AP lower
 * than the sheet with nothing to explain it.
 */
// Declarative: one line per reported field, in the log's key order.
function hitBreakdown(params: CalcSingleHitParams, s: HitStages) {
  const { raw, soak, ctx } = s;
  return {
    strengthBonus: raw.strengthBonus,
    talentDmgBonus: raw.talentDmgBonus,
    accurateDice: s.accurateDice,
    devastatingBonus: DEVASTATING_BONUS,
    maximalDmgBonus: raw.maximalDmgBonus,
    scatterMod: SCATTER_MOD,
    forceBonus: soak.forceBonus,
    ...(raw.predicate.dmg > 0 ? { predicateBonus: raw.predicate.dmg } : {}),
    ...(raw.predicate.pen > 0 ? { predicatePen: raw.predicate.pen } : {}),
    ...(raw.exposureDmg ? { exposureDmg: raw.exposureDmg } : {}),
    ...(ctx.exposed.pen ? { exposurePen: ctx.exposed.pen } : {}),
    ...(ctx.exposed.tb ? { exposureTB: ctx.exposed.tb } : {}),
    ...(s.landed.exposureDmgCap ? { exposureDmgCap: s.landed.exposureDmgCap } : {}),
    ...(s.outcome.exposureCrit ? { exposureCrit: s.outcome.exposureCrit } : {}),
    baseAP: soak.baseAP,
    machineAP: soak.machineAP,
    naturalAP: soak.naturalAP,
    basePen: params.pen,
    effectivePen: soak.effectivePen,
    baseTB: params.target.toughnessBonus,
    utMultiplier: soak.utMultiplier,
    fellingReduction: soak.fellingReduction,
    daemonicProt: soak.daemonicProt,
    damageFloorApplied: s.landed.damageFloorApplied,
    soakCapReduction: soak.soakCapReduction,
  };
}

/** Every component of a hit that never landed, zeroed. */
const UNLANDED = {
  dmgRoll: null,
  rawDamage: 0,
  ap: 0,
  effectiveAP: 0,
  coverAP: 0,
  tb: 0,
  effectiveDamage: 0,
} as const;
const NO_FURY = { righteousFury: false, rfConfirmed: false, rfRoll: 0, rfSeverity: 0 } as const;
const NO_BREAKDOWN = {
  crit: null,
  critSev: 0,
  strengthBonus: 0,
  talentDmgBonus: 0,
  accurateDice: 0,
  devastatingBonus: 0,
  maximalDmgBonus: 0,
  scatterMod: 0,
  forceBonus: 0,
  baseAP: 0,
  machineAP: 0,
  naturalAP: 0,
} as const;

/** The field caught the shot: nothing rolled past the d100, nothing soaked, nothing landed. */
function fieldBlockedHit(params: CalcSingleHitParams, damageType: string, field: ForceFieldCheck): HitResult {
  const { location, pen } = params;
  return {
    location,
    damageType,
    ...UNLANDED,
    penetration: pen,
    ...NO_FURY,
    ...field,
    ...NO_BREAKDOWN,
    basePen: pen,
    effectivePen: pen,
    baseTB: 0,
    utMultiplier: 0,
    fellingReduction: 0,
    daemonicProt: 0,
  };
}
